/* eslint eslint-comments/no-use: off */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {clientSideRelayFetchQueryRetainedWithCache} from '@github-ui/relay-query-cache/query-helpers'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueSingleSelectFieldPickerFieldQueryQuery} from '@github-ui/server-persisted-queries/types/IssueSingleSelectFieldPickerFieldQuery'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import type {IssueSingleSelectFieldPickerFieldQuery} from './__generated__/IssueSingleSelectFieldPickerFieldQuery.graphql'
import {createIssueSingleSelectFieldPickerItemLeadingVisual} from './IssueFieldLeadingVisual'

export const UNSET_ID = 'UNSET'

type IssueSingleSelectFieldPickerNode = Extract<
  NonNullable<IssueSingleSelectFieldPickerFieldQueryQuery['node']>,
  {__typename: 'IssueFieldSingleSelect'}
>
// RELAY-CLEANUP(IssueSingleSelectFieldPickerFieldQuery): RelayCompat widens Relay-produced nullables (the still-Relay
// fetchQuery source) to match the server-persisted-queries types. Remove once the producing query is off Relay.
export type IssueSingleSelectFieldPickerOptionNode = RelayCompat<
  NonNullable<IssueSingleSelectFieldPickerNode['options']>[number]
>

export type IssueFieldSingleSelectOption = Omit<IssueSingleSelectFieldPickerOptionNode, 'color'> & {
  color: 'BLUE' | 'GRAY' | 'GREEN' | 'ORANGE' | 'PINK' | 'PURPLE' | 'RED' | 'YELLOW'
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueSingleSelectFieldPickerFieldGraphqlQuery = graphql`
  query IssueSingleSelectFieldPickerFieldQuery($id: ID!) {
    node(id: $id) {
      ... on IssueFieldSingleSelect {
        options {
          id
          name
          description
          color
        }
      }
    }
  }
`

export type IssueSingleSelectFieldPickerProps = {
  title?: string
  width?: 'small' | 'medium' | 'large'
  fieldId: string
  fieldName?: string
  selectedOption: string | null
  readonly: boolean
  insidePortal?: boolean
  anchorElement: (
    props: React.HTMLAttributes<HTMLElement>,
    ref?: RefObject<HTMLButtonElement | null>,
  ) => React.ReactElement
  onSelectionChange: (selectedOption: IssueFieldSingleSelectOption | null) => void
  onOpen?: () => void
  onClose?: () => void
  onIssueUpdate?: () => void
  /**
   * Whether to render the issue type picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  ariaLabelledBy?: string
  /**
   * Whether this panel is lazily loaded, when the anchor element is clicked. When set to false, the
   * panel will be loaded immediately. This is used when we want to immediately select an option
   * after picking a single select field.
   */
  isLazy: boolean
  /**
   * Whether this panel should be open when initially rendered
   */
  shouldOpen?: boolean
}

type ItemPickerWrapperProps = Omit<IssueSingleSelectFieldPickerProps, 'isLazy'> & {
  values: IssueSingleSelectFieldPickerOptionNode[]
  isLoading: boolean
}

export function IssueSingleSelectFieldPicker({
  anchorElement,
  isLazy,
  shouldOpen = false,
  ...props
}: IssueSingleSelectFieldPickerProps) {
  if (isLazy && !shouldOpen) {
    return (
      <LazyItemPicker
        anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
        createChild={() => (
          <IssueSingleSelectFieldPickerFetcher
            anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
            {...props}
          />
        )}
      />
    )
  } else {
    return (
      <IssueSingleSelectFieldPickerFetcher
        anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
        {...props}
      />
    )
  }
}

function IssueSingleSelectFieldPickerFetcher({
  fieldId,
  anchorElement,
  ...props
}: Omit<IssueSingleSelectFieldPickerProps, 'isLazy'>) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueSingleSelectFieldPickerOptionNode[]>([])

  useEffect(() => {
    clientSideRelayFetchQueryRetainedWithCache<IssueSingleSelectFieldPickerFieldQuery>(
      {
        environment,
        query: IssueSingleSelectFieldPickerFieldGraphqlQuery,
        variables: {id: fieldId},
      },
      DEFAULT_RELAY_CACHE_TTL,
    ).subscribe({
      next: internalData => {
        const keys = internalData.node?.options as IssueSingleSelectFieldPickerOptionNode[]
        setData(keys ?? [])
        setIsLoading(false)
        setIsError(false)
      },
      error: () => {
        setIsError(true)
        setIsLoading(false)
      },
    })
  }, [environment, fetchKey, fieldId])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('fields'),
    anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <ItemPickerWrapper fieldId={fieldId} values={data} isLoading={isLoading} anchorElement={anchorElement} {...props} />
  )
}

function ItemPickerWrapper({
  title = LABELS.fieldSingleSelectHeader,
  width = 'medium',
  values,
  fieldName,
  selectedOption,
  onSelectionChange,
  onOpen,
  onClose,
  insidePortal,
  anchorElement,
  isLoading,
}: ItemPickerWrapperProps) {
  const [filter, setFilter] = useState('')

  // Use prop fieldName or fallback to default title
  const dynamicTitle = fieldName ? LABELS.fieldSingleSelectHeaderWithName(fieldName) : title

  const fetchedIssueFieldOptions = values.flatMap(a => (a ? [a] : [])) as IssueFieldSingleSelectOption[]

  const items = useMemo(() => {
    if (!filter) return fetchedIssueFieldOptions
    return fetchedIssueFieldOptions.filter(l => l.name && l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedIssueFieldOptions, filter])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((option: IssueFieldSingleSelectOption) => option.id || '', [])

  const convertToItemProps = useCallback(
    (option: IssueFieldSingleSelectOption): ExtendedItemProps<IssueFieldSingleSelectOption> => {
      return {
        id: option.id,
        text: option.name,
        description: option.description || '',
        descriptionVariant: 'block',
        leadingVisual: createIssueSingleSelectFieldPickerItemLeadingVisual(option.color),
        source: option,
      }
    },
    [],
  )

  const initialSelectedItems = useMemo(() => {
    if (selectedOption) {
      const selectedItem = fetchedIssueFieldOptions.find(item => item.name === selectedOption)
      if (selectedItem) {
        return [selectedItem]
      }
    }
    return []
  }, [fetchedIssueFieldOptions, selectedOption])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(() => items.length === 0, [items])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  return (
    <ItemPicker
      loading={isLoading}
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={dynamicTitle}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter option`}
      selectionVariant="single"
      onSelectionChange={selectedItems =>
        onSelectionChange(selectedItems && selectedItems.length > 0 && selectedItems[0] ? selectedItems[0] : null)
      }
      onOpen={onOpen}
      onClose={onClose}
      renderAnchor={anchorProps => anchorElement(anchorProps, anchorRef)}
      insidePortal={insidePortal}
      height={'large'}
      width={width}
      resultListAriaLabel={'Issue field option results'}
      triggerOpen
      selectPanelRef={anchorRef}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No option found',
              variant: 'empty',
              body: 'Try searching with a different query for results.',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}
